import React, { useContext, useEffect, useState } from 'react'
import { Alert, Button, FormControlLabel, Paper, Snackbar, Switch, Typography, useTheme } from '@mui/material'
import { ReservationContext } from '../../context/ReservationContext'
import { updateScheduleSetting, fetchAdminSeats, updateSeatStatus, getSeatDetails } from '../backend'
import useMediaQuery from '@mui/material/useMediaQuery'
import AdminSeatGrid from './AdminSeatGrid'
import SeatDetailsDialog from './SeatDetailsDialog'
import ConfirmResetDialog from './ConfirmResetDialog'
import BookingInfoDialog from './BookingInfoDialog'
import { LoadingSkeleton } from '../LoadingSkeleton'
import AdminSeatHeader from './AdminSeatHeader'
import AdminReservationsTable from './AdminReservationsTable'

// ---------------------------------------------------------------------------
// CSV / PDF export helpers (no extra dependencies)
// ---------------------------------------------------------------------------

/** Escape a value for CSV – wraps in quotes if it contains comma, quote or newline */
function csvEscape(val) {
  if (val == null) return ''
  const s = String(val)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

function exportCSV(reservedSeats, showName, scheduleName) {
  const headers = ['Siège', 'Nom complet', 'Email', 'Téléphone', 'Statut paiement']
  const rows = reservedSeats.map((seat) => {
    const ad = seat.admin_data || {}
    return [
      `${seat.row}-${seat.seat}`,
      `${ad.firstName || ''} ${ad.lastName || ''}`.trim() || '—',
      ad.email || '—',
      ad.phone || '—',
      ad.paid || ''
    ]
  })
  const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n')
  const BOM = '﻿' // UTF-8 BOM so Excel opens accented chars correctly
  downloadFile(BOM + csv, `${showName}_${scheduleName}_reservations.csv`, 'text/csv;charset=utf-8')
}

function exportPDF(reservedSeats, showName, scheduleName) {
  // Build a hidden printable section
  const printDiv = document.createElement('div')
  printDiv.id = '_print_export'
  printDiv.innerHTML = `
    <style>
      @media print {
        body * { visibility: hidden !important; }
        #_print_export, #_print_export * { visibility: visible !important; }
        #_print_export {
          position: absolute; left: 0; top: 0; width: 100%;
        }
        table { border-collapse: collapse; width: 100%; font-size: 10pt; }
        th, td { border: 1px solid #333; padding: 4px 8px; text-align: left; }
        th { background: #eee; }
      }
    </style>
    <h2>${showName} — ${scheduleName}</h2>
    <p>Date d'export : ${new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
    <table>
      <thead><tr><th>Siège</th><th>Nom complet</th><th>Email</th><th>Téléphone</th><th>Statut paiement</th></tr></thead>
      <tbody>${reservedSeats
        .map((seat) => {
          const ad = seat.admin_data || {}
          return `<tr><td>${seat.row}-${seat.seat}</td><td>${ad.firstName || ''} ${ad.lastName || ''}</td><td>${ad.email || ''}</td><td>${ad.phone || ''}</td><td>${ad.paid || ''}</td></tr>`
        })
        .join('')}</tbody>
    </table>`
  document.body.appendChild(printDiv)
  window.print()
  // Clean up after print dialog closes (give a small delay so print renders first)
  setTimeout(() => {
    const el = document.getElementById('_print_export')
    if (el && el.parentNode) el.parentNode.removeChild(el)
  }, 1000)
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 100)
}

// ---------------------------------------------------------------------------

export default function AdminSeatManagement() {
  const [loading, setLoading] = useState(true)
  const [seats, setSeats] = useState([])
  const [selectedSeat, setSelectedSeat] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)
  const [bookingInfoOpen, setBookingInfoOpen] = useState(false)
  const [bookingInfo, setBookingInfo] = useState({ firstName: '', lastName: '', email: '', phone: '', paid: '' })
  const [showId, setShowId] = useState(3)
  // default showId = 3 = ex-voto
  const [scheduleId, setScheduleId] = useState(101)
  const [refreshing, setRefreshing] = useState(false)
  const [snackMessage, setSnackMessage] = useState({ message: '', severity: 'success' })
  const [filterName, setFilterName] = useState('')
  const [enforceSeatGaps, setEnforceSeatGapsLocal] = useState(true)

  const { shows, setShowId: ctxSetShowId } = useContext(ReservationContext)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (showId && scheduleId) {
      setLoading(true)
      fetchAdminSeats(showId, scheduleId).then((res) => {
        setSeats(res)
        setLoading(false)
      })
    }
  }, [showId, scheduleId])

  useEffect(() => {
    if (showId) {
      const show = shows.find((s) => s.id === showId)
      if (show?.schedules?.length > 0) {
        setScheduleId(show.schedules[0].id)
      } else {
        setScheduleId(null)
      }
    }
  }, [showId, shows])

  // Load enforce_seat_gaps from the selected schedule's data (from DB via fetchShows)
  useEffect(() => {
    if (scheduleId) {
      const show = shows.find((s) => s.id === showId)
      const sched = (show?.schedules || []).find((sc) => sc.id === scheduleId)
      setEnforceSeatGapsLocal(sched?.enforce_seat_gaps ?? true)
    }
  }, [scheduleId, showId, shows])

  const toggleEnforceSeatGaps = async () => {
    const newValue = !enforceSeatGaps
    setEnforceSeatGapsLocal(newValue)
    try {
      await updateScheduleSetting({
        show_id: showId,
        schedule_id: scheduleId,
        key: 'enforce_seat_gaps',
        value: newValue
      })
      // Refresh shows in context so SeatSelection sees the new value too
      const res = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : ''}/api/shows`)
      if (res.ok) {
        const data = await res.json()
        sessionStorage.setItem('shows', JSON.stringify(data))
        ctxSetShowId(showId) // trigger re-render of context's shows sync
      }
      setSnackMessage({ message: 'Paramètre mis à jour', severity: 'success' })
    } catch (error) {
      setEnforceSeatGapsLocal(!newValue)
      setSnackMessage({
        message: 'Erreur lors de la mise à jour: ' + (error?.body?.message || error.status),
        severity: 'error'
      })
    }
  }

  const handleSeatClick = async (seat) => {
    setLoading(true)
    const details = await getSeatDetails({ seat_id: seat.id, show_id: showId, schedule_id: scheduleId })
    setLoading(false)
    setSelectedSeat(details.success ? { ...seat, ...details.data } : seat)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedSeat(null)
  }

  const applySeatUpdate = async (status, adminData = null) => {
    if (!selectedSeat) return
    const previousStatus = selectedSeat.status === 'occupied' ? 2 : selectedSeat.status === 'selected' ? 1 : 0
    try {
      await updateSeatStatus({
        seat_id: selectedSeat.id,
        status,
        show_id: showId,
        schedule_id: scheduleId,
        admin_data: adminData,
        previousStatus
      })
      setSnackMessage({ message: 'Status du siège mis à jour', severity: 'success' })
    } catch (error) {
      console.log(error)
      setSnackMessage({
        message: 'Erreur lors de la mise à jour du siège: ' + (error?.body?.message ? error.body.message : error.status),
        severity: 'error'
      })
      return
    }
    setLoading(true)
    fetchAdminSeats(showId, scheduleId).then((res) => {
      setSeats(res)
      setLoading(false)
    })
    handleCloseDialog()
    setConfirmResetOpen(false)
    setBookingInfoOpen(false)
    setBookingInfo({ firstName: '', lastName: '', email: '', phone: '', paid: '' })
  }

  const handleUpdateAdminData = async (newAdminData) => {
    if (!selectedSeat) return
    const currentStatus = selectedSeat.status === 'occupied' ? 2 : selectedSeat.status === 'selected' ? 1 : 0
    const response = await updateSeatStatus({
      seat_id: selectedSeat.id,
      status: currentStatus,
      show_id: showId,
      schedule_id: scheduleId,
      admin_data: newAdminData,
      previousStatus: currentStatus
    })
    if (response.success) {
      setSelectedSeat((prev) => ({ ...prev, admin_data: newAdminData }))
      return response
    } else {
      setSnackMessage({ message: 'Error: ' + response.message, severity: 'error' })
      const refreshed = await getSeatDetails({ seat_id: selectedSeat.id, show_id: showId, schedule_id: scheduleId })
      if (refreshed.success) {
        setSelectedSeat((prev) => ({ ...prev, ...refreshed.data }))
      }
      return null
    }
  }

  const handleSeatAction = () => {
    if (!selectedSeat) return
    if (selectedSeat.status === 'occupied') {
      setConfirmResetOpen(true)
    } else {
      // Pre-fill form with existing admin_data if available
      const ad = selectedSeat.admin_data
      setBookingInfo({
        firstName: ad?.firstName || '',
        lastName: ad?.lastName || '',
        email: ad?.email || '',
        phone: ad?.phone || '',
        paid: ad?.paid || ''
      })
      setBookingInfoOpen(true)
    }
  }

  const updateBookingInfo = (field, value) => setBookingInfo((prev) => ({ ...prev, [field]: value }))

  const refreshSeats = async () => {
    setRefreshing(true)
    const res = await fetchAdminSeats(showId, scheduleId)
    setSeats(res)
    setRefreshing(false)
  }

  // Compute reserved seats once per render so both table and export share the same data
  const allReserved = seats.filter((s) => s.status === 'occupied' && s.admin_data)
  const filteredReserved = allReserved.filter((s) => {
    if (!filterName) return true
    const ad = s.admin_data
    const stringValues = Object.keys(ad)
      .filter((k) => typeof ad[k] === 'string')
      .map((k) => ad[k].toLowerCase())
    return stringValues.some((v) => v.includes(filterName.toLowerCase()))
  })

  // Resolve show / schedule display names for export filenames
  const selectedShow = shows.find((s) => s.id === showId)
  const schedObj = (selectedShow?.schedules || []).find((s) => s.id === scheduleId)
  const showName = selectedShow?.name || 'spectacle'
  const scheduleName = schedObj?.nom || `plage_${scheduleId}`

  const handleExportCSV = () => exportCSV(filteredReserved, showName, scheduleName)
  const handleExportPDF = () => exportPDF(filteredReserved, showName, scheduleName)

  return (
    <Paper elevation={3} sx={{ minHeight: '400px', p: { xs: 1, sm: 2 }, borderRadius: 10, position: 'relative' }}>
      <AdminSeatHeader
        shows={shows}
        showId={showId}
        scheduleId={scheduleId}
        onShowChange={setShowId}
        onScheduleChange={setScheduleId}
        onRefresh={refreshSeats}
        refreshing={refreshing}
      />

      {/* Enforce seat gaps toggle */}
      <Paper elevation={0} sx={{ bgcolor: 'background.default', p: 1.5, mt: 2, mb: 1, borderRadius: 2 }}>
        <FormControlLabel
          control={<Switch checked={enforceSeatGaps} onChange={toggleEnforceSeatGaps} color='primary' />}
          label={<Typography variant='body2'>Empêcher les places isolées</Typography>}
        />
      </Paper>

      {loading ? (
        <LoadingSkeleton text={'Chargement ...'} />
      ) : (
        <>
          <AdminSeatGrid showId={showId} scheduleId={scheduleId} loading={loading} seats={seats} onSeatClick={handleSeatClick} />

          {seats.some((s) => s.status === 'occupied' && (s.admin_data || s.booking)) && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, mt: 3, mb: 1, flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Filtrer par nom :</label>
                <input
                  type='text'
                  placeholder='Rechercher...'
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  style={{ padding: 4, fontSize: '0.875rem', width: 180 }}
                />
                <Button size='small' variant='outlined' onClick={handleExportCSV} sx={{ ml: 'auto' }}>
                  Excel
                </Button>
                <Button size='small' variant='outlined' onClick={handleExportPDF}>
                  PDF
                </Button>
              </div>

              <AdminReservationsTable reservedSeats={filteredReserved} />
            </>
          )}

          <SeatDetailsDialog
            selectedSeat={selectedSeat}
            isSmallScreen={isSmallScreen}
            theme={theme}
            open={dialogOpen}
            onClose={handleCloseDialog}
            onSeatAction={handleSeatAction}
            onEditAdminData={handleUpdateAdminData}
            showId={showId}
            scheduleId={scheduleId}
          />

          <ConfirmResetDialog
            open={confirmResetOpen}
            seat={selectedSeat}
            onConfirm={() => applySeatUpdate(0)}
            onCancel={() => setConfirmResetOpen(false)}
          />

          <BookingInfoDialog
            seat={selectedSeat}
            bookingInfo={bookingInfo}
            open={bookingInfoOpen}
            onChange={updateBookingInfo}
            onSubmit={() => applySeatUpdate(2, bookingInfo)}
            onCancel={() => setBookingInfoOpen(false)}
          />
        </>
      )}

      <Snackbar
        open={!!snackMessage.message}
        autoHideDuration={4000}
        onClose={() => setSnackMessage({ message: '', severity: 'success' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackMessage({ message: '', severity: 'success' })}
          severity={snackMessage.severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

AdminSeatManagement.propTypes = {}
